import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { EnvelopeSimple, PaperPlaneTilt, Lightning, CheckCircle, Clock, Warning, DeviceMobile, Trash, PencilSimple, X, Check, CalendarPlus, ClockClockwise } from '@phosphor-icons/react';

export default function HerinneringenPage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [herinneringen, setHerinneringen] = useState([]);
  const [geplande, setGeplande] = useState([]);
  const [inst, setInst] = useState({});
  const [bulkResult, setBulkResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [emailSending, setEmailSending] = useState('');

  // Selectie-state voor de "Openstaande facturen"-tabel
  const [openSelectie, setOpenSelectie] = useState({});
  // Selectie-state voor de "Verstuurde herinneringen"-tabel
  const [verzondenSelectie, setVerzondenSelectie] = useState({});

  // Bewerken-modal state. `mode`='bulk' (meerdere geselecteerde) of 'single' (1 specifieke factuur)
  // `plannen` = false (nu verzenden) of true (plannen voor later). `plan_datum` = ISO local datetime.
  const [bewerkModal, setBewerkModal] = useState(null);

  const load = () => {
    API.get('/documenten').then(r => setDocs(r.data)).catch(() => {});
    API.get('/klanten').then(r => setKlanten(r.data)).catch(() => {});
    API.get('/herinneringen').then(r => setHerinneringen(r.data)).catch(() => {});
    API.get('/herinneringen/gepland').then(r => setGeplande(r.data || [])).catch(() => {});
    API.get('/instellingen').then(r => setInst(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const kNaam = (kid) => { const k = klanten.find(x => x.id === kid); return k ? (k.bedrijf || `${k.voorn} ${k.achtern}`) : kid || '-'; };
  const kEmail = (kid) => klanten.find(x => x.id === kid)?.email || '';
  const dagLaat = (verval) => { const v = new Date(verval), n = new Date(); n.setHours(0,0,0,0); v.setHours(0,0,0,0); return Math.floor((n-v)/86400000); };

  const open = docs.filter(f => f.type === 'factuur' && (f.status === 'openstaand' || f.status === 'vervallen'));
  const vervallen = open.filter(f => dagLaat(f.verval) > 0);
  const totaalOpen = open.reduce((s, f) => s + (f.totaal || 0), 0);
  const emailsSent = herinneringen.filter(h => h.email_verstuurd).length;

  const hasSendGrid = !!inst.sendgrid_key;
  const autoEmail = !!inst.auto_herinnering_email;
  const hasSMS = !!(inst.twilio_actief || inst.msgbird_actief);
  const smsSent = herinneringen.filter(h => h.sms_verstuurd).length;

  // Selectie-helpers
  const openSelectieIds = useMemo(() => Object.keys(openSelectie).filter(k => openSelectie[k]), [openSelectie]);
  const verzondenSelectieIds = useMemo(() => Object.keys(verzondenSelectie).filter(k => verzondenSelectie[k]), [verzondenSelectie]);

  const toggleOpenAll = () => {
    if (openSelectieIds.length === open.length && open.length > 0) {
      setOpenSelectie({});
    } else {
      const all = {};
      open.forEach(f => { all[f.id] = true; });
      setOpenSelectie(all);
    }
  };
  const toggleVerzondenAll = () => {
    if (verzondenSelectieIds.length === herinneringen.length && herinneringen.length > 0) {
      setVerzondenSelectie({});
    } else {
      const all = {};
      herinneringen.forEach(h => { all[h.id] = true; });
      setVerzondenSelectie(all);
    }
  };

  const verstuurEnkel = async (f) => {
    const stap = herinneringen.filter(h => h.fac_id === f.id).length + 1;
    const stapNm = ['1e Herinnering', '2e Herinnering', 'Aanmaning'][Math.min(stap - 1, 2)];

    if (hasSendGrid) {
      await API.post('/herinneringen', { fac_id: f.id, klant: kNaam(f.klant_id), stap, stapNm, bedrag: f.totaal, verval: f.verval });
      load();
    } else {
      await API.post('/herinneringen', { fac_id: f.id, klant: kNaam(f.klant_id), stap, stapNm, bedrag: f.totaal, verval: f.verval });
      const late = dagLaat(f.verval);
      const tekst = `Geachte ${kNaam(f.klant_id)},\n\nFactuur ${f.id} voor ${fmt(f.totaal)} is nog niet voldaan.\nVervaldatum: ${f.verval}${late > 0 ? `\nDagen te laat: ${late}` : ''}\n\nGelieve te betalen op ${inst.iban || '<IBAN>'}.\n\nMvg,\n${inst.naam || 'Emergent'}`;
      const sub = encodeURIComponent(`${stapNm} - ${f.id}`);
      window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(kEmail(f.klant_id))}&su=${sub}&body=${encodeURIComponent(tekst)}`, '_blank');
      load();
    }
  };

  // Verzend bulk: ofwel ALLES (open.length), ofwel enkel de geselecteerde
  const verstuurAlles = async () => {
    setSending(true); setBulkResult(null);
    try {
      let r;
      if (openSelectieIds.length > 0) {
        r = await API.post('/herinneringen/verzend-selectie', { fac_ids: openSelectieIds });
        setBulkResult(r.data);
        setOpenSelectie({}); // selectie wissen na verzenden
      } else {
        r = await API.post('/herinneringen/bulk');
        setBulkResult(r.data);
      }
      load();
    } catch {
      setBulkResult({ created: 0, emails_sent: 0 });
    }
    setSending(false);
  };

  // Bewerken-modal openen met de geselecteerde facturen (of alle openstaande als niets geselecteerd)
  const openBewerkModal = (fac_ids_override = null) => {
    const fac_ids = fac_ids_override !== null
      ? fac_ids_override
      : (openSelectieIds.length > 0 ? openSelectieIds : open.map(f => f.id));
    if (fac_ids.length === 0) return;
    const defaultTekst = inst.herinnering_1_tekst || `Geachte {klant},\n\nWij stellen u beleefd in kennis dat factuur {document_nr} ten bedrage van {outstanding} nog niet werd voldaan.\n\nGelieve dit bedrag zo spoedig mogelijk over te maken.\n\nMet vriendelijke groet,\n{bedrijfsnaam}`;
    const defaultSubj = `Herinnering - {document_nr} - ${inst.naam || 'Emergent'}`;
    // Default plan-datum = morgen 9u00 (Brussels)
    const morgen9 = new Date();
    morgen9.setDate(morgen9.getDate() + 1);
    morgen9.setHours(9, 0, 0, 0);
    const planLocal = morgen9.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
    setBewerkModal({
      fac_ids,
      mode: fac_ids.length === 1 ? 'single' : 'bulk',
      onderwerp: defaultSubj,
      tekst: defaultTekst,
      plannen: false,
      plan_datum: planLocal,
    });
  };

  const verzendAangepast = async () => {
    if (!bewerkModal) return;
    // Parse extra CC-emails: komma-gescheiden, trim, valideer @
    const ccLijst = (bewerkModal.cc_emails || '')
      .split(/[,;]/)
      .map(e => e.trim())
      .filter(e => e.includes('@'));
    setSending(true); setBulkResult(null);
    try {
      if (bewerkModal.plannen) {
        // Plan voor later
        await API.post('/herinneringen/plannen', {
          fac_ids: bewerkModal.fac_ids,
          plan_datum: bewerkModal.plan_datum,
          aangepast_onderwerp: bewerkModal.onderwerp,
          aangepaste_tekst: bewerkModal.tekst,
          cc_emails: ccLijst,
        });
        setBulkResult({ created: 0, emails_sent: 0, gepland: bewerkModal.fac_ids.length, plan_datum: bewerkModal.plan_datum });
      } else {
        // Direct verzenden
        const r = await API.post('/herinneringen/verzend-selectie', {
          fac_ids: bewerkModal.fac_ids,
          aangepast_onderwerp: bewerkModal.onderwerp,
          aangepaste_tekst: bewerkModal.tekst,
          cc_emails: ccLijst,
        });
        setBulkResult(r.data);
      }
      setBewerkModal(null);
      setOpenSelectie({});
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Verzenden mislukt');
    }
    setSending(false);
  };

  // Wissen — voor "Verstuurde herinneringen" tabel
  const wissenGeselecteerd = async () => {
    if (verzondenSelectieIds.length === 0) return;
    if (!window.confirm(`${verzondenSelectieIds.length} herinnering(en) verwijderen? Deze actie kan niet ongedaan gemaakt worden.`)) return;
    try {
      await API.post('/herinneringen/bulk-delete', { ids: verzondenSelectieIds });
      setVerzondenSelectie({});
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  // Annuleer een geplande herinnering (status → canceled)
  const annuleerGeplande = async (gid) => {
    if (!window.confirm('Geplande herinnering annuleren?')) return;
    try {
      await API.delete(`/herinneringen/gepland/${gid}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Annuleren mislukt');
    }
  };

  // Datum-formatter voor de geplande-tabel
  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const verstuurEmailHerinnering = async (herin) => {
    setEmailSending(herin.id);
    try { await API.post(`/herinneringen/${herin.id}/email`); load(); }
    catch (e) { alert(e.response?.data?.detail || 'E-mail verzenden mislukt'); }
    setEmailSending('');
  };

  const stapBadge = (stap) => {
    if (stap >= 3) return 'bg-red-100 text-red-800 border-red-200';
    if (stap === 2) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  const verstuurKnopLabel = openSelectieIds.length > 0
    ? `Verzend geselecteerde (${openSelectieIds.length})`
    : `Verzend allemaal (${open.length})`;

  return (
    <div data-testid="herinneringen-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="page-heading mb-0">Betalingsherinneringen</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {open.length > 0 && (
            <>
              <button
                className="btn btn-secondary inline-flex items-center gap-1.5"
                onClick={openBewerkModal}
                disabled={sending}
                title="Bewerk de e-mail-tekst voor de geselecteerde (of alle) facturen vooraleer te verzenden"
                data-testid="bewerk-herin-btn"
              >
                <PencilSimple size={14} weight="bold" /> Bewerken{openSelectieIds.length > 0 ? ` (${openSelectieIds.length})` : ''}
              </button>
              <button
                className="btn btn-primary inline-flex items-center gap-1.5"
                onClick={verstuurAlles}
                disabled={sending}
                data-testid="bulk-herin-btn"
              >
                <Lightning size={16} weight="bold" /> {sending ? 'Verwerken...' : verstuurKnopLabel}
              </button>
            </>
          )}
        </div>
      </div>

      {/* SendGrid status banner */}
      {hasSendGrid && autoEmail ? (
        <div className="mb-4 p-3 border border-green-200 bg-green-50 text-green-700 text-sm flex items-center gap-2" data-testid="sendgrid-active-banner">
          <CheckCircle size={18} weight="bold" />
          <span><strong>Automatisch e-mailen actief</strong> — Herinneringen worden automatisch verstuurd via SendGrid bij bulk verwerking.</span>
        </div>
      ) : hasSendGrid ? (
        <div className="mb-4 p-3 border border-blue-200 bg-blue-50 text-blue-700 text-sm flex items-center gap-2" data-testid="sendgrid-manual-banner">
          <EnvelopeSimple size={18} weight="bold" />
          <span>SendGrid geconfigureerd — klik op het e-mail icoon om individueel te versturen. Zet <strong>auto e-mail</strong> aan in Instellingen &gt; Herinneringen voor automatisch versturen.</span>
        </div>
      ) : (
        <div className="mb-4 p-3 border border-gray-200 bg-gray-50 text-gray-500 text-sm flex items-center gap-2" data-testid="sendgrid-off-banner">
          <Warning size={18} />
          <span>SendGrid niet geconfigureerd — herinneringen worden via Gmail geopend. Configureer SendGrid in Instellingen &gt; Integraties voor automatische e-mails.</span>
        </div>
      )}

      {bulkResult && (
        <div className="mb-4 p-3 border border-green-200 bg-green-50 text-green-800 text-sm" data-testid="bulk-result">
          {bulkResult.gepland > 0 ? (
            <>
              <strong>📅 {bulkResult.gepland} herinnering(en) gepland</strong> voor verzending op <strong>{fmtDateTime(bulkResult.plan_datum)}</strong>. Kan geannuleerd worden via &quot;Geplande herinneringen&quot; hieronder.
            </>
          ) : (
            <>
              {bulkResult.created} herinnering(en) aangemaakt.
              {bulkResult.emails_sent > 0 && <strong> {bulkResult.emails_sent} e-mail(s) automatisch verstuurd via SendGrid.</strong>}
              {bulkResult.sms_sent > 0 && <strong> {bulkResult.sms_sent} SMS verzonden via {inst.twilio_actief ? 'Twilio' : 'MessageBird'}.</strong>}
              {bulkResult.created > 0 && bulkResult.emails_sent === 0 && !bulkResult.sms_sent && hasSendGrid && ' Geen e-mails/SMS verstuurd (auto e-mail staat uit of klant heeft geen contactgegevens).'}
            </>
          )}
        </div>
      )}

      {open.length > 0 && (
        <div className="alert-warn" data-testid="open-facturen-alert">{open.length} factuur/facturen wachten op herinnering. {vervallen.length > 0 && <strong>{vervallen.length} zijn vervallen!</strong>}</div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2 mb-4" data-testid="reminder-summary-grid">
        <div className="kpi-card"><div className="kpi-label">Openstaand totaal</div><div className="kpi-value text-[#E5B300]">{fmt(totaalOpen)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Vervallen facturen</div><div className="kpi-value text-[#E02424]">{vervallen.length}</div></div>
        <div className="kpi-card"><div className="kpi-label">Herinneringen aangemaakt</div><div className="kpi-value text-[#002FA7]">{herinneringen.length}</div></div>
        <div className="kpi-card"><div className="kpi-label">E-mails verstuurd</div><div className="kpi-value text-[#059669]">{emailsSent}</div></div>
        <div className="kpi-card"><div className="kpi-label">SMS verzonden</div><div className="kpi-value text-[#7C3AED]">{smsSent}</div></div>
      </div>

      {/* SMS provider banner */}
      {hasSMS && (
        <div className="mb-4 p-3 border border-purple-200 bg-purple-50 text-purple-800 text-sm flex items-center gap-2" data-testid="sms-active-banner">
          <DeviceMobile size={18} weight="bold" />
          <span><strong>SMS-herinneringen actief</strong> via {inst.twilio_actief ? 'Twilio' : 'MessageBird'} — verzending op basis van de checkboxes in Instellingen › Integraties (stap 1/2/3 + betaalbevestiging).</span>
        </div>
      )}

      {/* Geplande herinneringen — Plannen voor later */}
      {geplande.length > 0 && (
        <div className="section-card mb-4" data-testid="geplande-sectie">
          <div className="section-header flex items-center gap-2">
            <ClockClockwise size={16} weight="bold" className="text-purple-600" />
            <span className="section-title">Geplande herinneringen</span>
            <span className="text-xs text-gray-500 ml-2">verzending op gepland tijdstip — kan geannuleerd worden zolang status &quot;Wachtend&quot; is</span>
          </div>
          <div className="section-body">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Plan-datum</th>
                  <th>Aantal facturen</th>
                  <th>Aangemaakt</th>
                  <th>Aangepaste tekst?</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {geplande.map(g => (
                  <tr key={g.id} data-testid={`gepland-row-${g.id}`}>
                    <td>
                      {g.status === 'pending' && <span className="badge bg-amber-100 text-amber-800 border border-amber-300">⏳ Wachtend</span>}
                      {g.status === 'sent' && <span className="badge bg-green-100 text-green-800 border border-green-300">✓ Verzonden</span>}
                      {g.status === 'canceled' && <span className="badge bg-gray-100 text-gray-600 border border-gray-300">Geannuleerd</span>}
                      {g.status === 'failed' && <span className="badge bg-red-100 text-red-800 border border-red-300">Mislukt</span>}
                    </td>
                    <td className="font-mono text-xs">{fmtDateTime(g.plan_datum)}</td>
                    <td><strong>{g.aantal}</strong></td>
                    <td className="text-xs text-gray-500">{fmtDateTime(g.aangemaakt_op)}</td>
                    <td>{g.aangepaste_tekst || g.aangepast_onderwerp ? <span className="text-[10px] text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">✏️ Aangepast</span> : <span className="text-xs text-gray-400">Standaard template</span>}</td>
                    <td className="text-right">
                      {g.status === 'pending' && (
                        <button
                          className="btn btn-sm bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 inline-flex items-center gap-1"
                          onClick={() => annuleerGeplande(g.id)}
                          data-testid={`annuleer-gepland-${g.id}`}
                        >
                          <X size={12} weight="bold" /> Annuleren
                        </button>
                      )}
                      {g.status === 'sent' && (
                        <span className="text-xs text-gray-500">
                          {g.emails_sent || 0} mails • {g.created_count || 0} herin.
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Openstaande facturen */}
      <div className="section-card mb-4">
        <div className="section-header flex items-center justify-between">
          <span className="section-title">Openstaande en vervallen facturen</span>
          {openSelectieIds.length > 0 && (
            <span className="text-xs font-bold text-[#002FA7] bg-blue-50 border border-blue-200 px-2 py-1 rounded-full" data-testid="open-selectie-count">
              {openSelectieIds.length} geselecteerd
            </span>
          )}
        </div>
        <div className="section-body">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-10 text-center">
                  <input
                    type="checkbox"
                    className="cursor-pointer accent-[#002FA7]"
                    checked={open.length > 0 && openSelectieIds.length === open.length}
                    onChange={toggleOpenAll}
                    title={openSelectieIds.length === open.length ? 'Alle deselecteren' : 'Alle selecteren'}
                    data-testid="open-select-all"
                  />
                </th>
                <th>Factuur</th><th>Klant</th><th className="right">Bedrag</th><th>Vervaldag</th><th>Te laat</th><th>Volgende stap</th><th></th>
              </tr>
            </thead>
            <tbody>
              {open.map(f => {
                const late = dagLaat(f.verval);
                const stap = herinneringen.filter(h => h.fac_id === f.id).length + 1;
                const isChecked = !!openSelectie[f.id];
                return (
                  <tr
                    key={f.id}
                    className={`cursor-pointer transition-colors ${isChecked ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-[#F0F4FF]'}`}
                    onClick={() => navigate(`/documenten/${f.id}/bewerk`)}
                    data-testid={`herin-openstaand-row-${f.id}`}
                  >
                    <td onClick={e => e.stopPropagation()} className="text-center">
                      <input
                        type="checkbox"
                        className="cursor-pointer accent-[#002FA7]"
                        checked={isChecked}
                        onChange={e => setOpenSelectie(p => ({ ...p, [f.id]: e.target.checked }))}
                        data-testid={`open-select-${f.id}`}
                      />
                    </td>
                    <td><span className="doc-nr">{f.id}</span></td>
                    <td>{kNaam(f.klant_id)}</td>
                    <td className="right font-medium">{fmt(f.totaal)}</td>
                    <td>{f.verval}</td>
                    <td>{late > 0 ? <span className="font-bold text-[#E02424]">+{late} dagen</span> : <span className="text-[#008A27]">Op tijd</span>}</td>
                    <td><span className={`badge border ${stapBadge(stap)}`}>{['1e Herinnering', '2e Herinnering', 'Aanmaning'][Math.min(stap - 1, 2)]}</span></td>
                    <td onClick={e => e.stopPropagation()} className="flex items-center gap-1.5">
                      <button
                        className="text-[#002FA7] hover:bg-blue-100 p-1.5 rounded transition-colors"
                        onClick={() => openBewerkModal([f.id])}
                        title="Bewerk e-mail-tekst voor enkel deze factuur"
                        data-testid={`herin-bewerk-row-${f.id}`}
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => verstuurEnkel(f)} data-testid={`herin-btn-${f.id}`}>
                        {hasSendGrid ? <><EnvelopeSimple size={14} /> Verstuur</> : <><PaperPlaneTilt size={14} /> Via Gmail</>}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {open.length === 0 && <tr><td colSpan="8" className="text-center text-gray-400 py-8">Geen openstaande facturen. Alle facturen zijn betaald!</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Verstuurde herinneringen */}
      <div className="section-card">
        <div className="section-header flex items-center justify-between flex-wrap gap-2">
          <span className="section-title">Verstuurde herinneringen</span>
          <div className="flex items-center gap-2">
            {verzondenSelectieIds.length > 0 && (
              <>
                <span className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-full" data-testid="verzonden-selectie-count">
                  {verzondenSelectieIds.length} geselecteerd
                </span>
                <button
                  className="btn btn-sm bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1.5"
                  onClick={wissenGeselecteerd}
                  data-testid="wissen-geselecteerd-btn"
                >
                  <Trash size={13} weight="bold" /> Wissen ({verzondenSelectieIds.length})
                </button>
              </>
            )}
          </div>
        </div>
        <div className="section-body">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-10 text-center">
                  <input
                    type="checkbox"
                    className="cursor-pointer accent-red-600"
                    checked={herinneringen.length > 0 && verzondenSelectieIds.length === herinneringen.length}
                    onChange={toggleVerzondenAll}
                    title="Alle (de)selecteren"
                    data-testid="verzonden-select-all"
                  />
                </th>
                <th>Datum</th><th>Factuur</th><th>Klant</th><th className="right">Bedrag</th><th>Stap</th><th>E-mail</th><th>SMS</th><th></th>
              </tr>
            </thead>
            <tbody>
              {herinneringen.slice().reverse().map((h) => {
                const isChecked = !!verzondenSelectie[h.id];
                return (
                  <tr
                    key={h.id}
                    className={`cursor-pointer transition-colors ${isChecked ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-[#F0F4FF]'}`}
                    onClick={() => navigate(`/documenten/${h.fac_id}/bewerk`)}
                    data-testid={`herin-verstuurd-row-${h.id}`}
                  >
                    <td onClick={e => e.stopPropagation()} className="text-center">
                      <input
                        type="checkbox"
                        className="cursor-pointer accent-red-600"
                        checked={isChecked}
                        onChange={e => setVerzondenSelectie(p => ({ ...p, [h.id]: e.target.checked }))}
                        data-testid={`verzonden-select-${h.id}`}
                      />
                    </td>
                    <td>{h.datum}</td>
                    <td><span className="doc-nr">{h.fac_id}</span></td>
                    <td>{h.klant}</td>
                    <td className="right">{fmt(h.bedrag)}</td>
                    <td><span className={`badge border ${stapBadge(h.stap)}`}>{h.stapNm}</span></td>
                    <td>
                      {h.email_verstuurd ? (
                        <span className="flex items-center gap-1 text-xs text-green-600" data-testid={`email-sent-${h.id}`}>
                          <CheckCircle size={14} weight="bold" /> Verstuurd
                          {h.klant_email && <span className="text-gray-400 ml-1">({h.klant_email})</span>}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-gray-400" data-testid={`email-pending-${h.id}`}>
                          <Clock size={14} /> Niet verstuurd
                        </span>
                      )}
                    </td>
                    <td>
                      {h.sms_verstuurd ? (
                        <span className="flex items-center gap-1 text-xs text-purple-700" data-testid={`sms-sent-${h.id}`}>
                          <DeviceMobile size={14} weight="bold" /> Verzonden
                          {h.klant_gsm && <span className="text-gray-400 ml-1">({h.klant_gsm})</span>}
                        </span>
                      ) : h.klant_gsm ? (
                        <span className="flex items-center gap-1 text-xs text-gray-400"><Clock size={14} /> —</span>
                      ) : (
                        <span className="text-xs text-gray-300">geen GSM</span>
                      )}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      {!h.email_verstuurd && hasSendGrid && (
                        <button className="btn btn-ghost btn-sm text-[#002FA7]"
                          onClick={() => verstuurEmailHerinnering(h)}
                          disabled={emailSending === h.id}
                          data-testid={`send-email-${h.id}`}>
                          <EnvelopeSimple size={14} /> {emailSending === h.id ? 'Bezig...' : 'E-mail'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {herinneringen.length === 0 && <tr><td colSpan="9" className="text-center text-gray-400 py-6">Nog geen herinneringen verstuurd</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bewerken-modal — pas onderwerp en tekst aan vóór bulk-verzenden */}
      {bewerkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setBewerkModal(null)} data-testid="bewerk-modal">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <PencilSimple size={18} weight="bold" className="text-[#002FA7]" />
                  {bewerkModal.mode === 'single' ? `Bewerken voor ${bewerkModal.fac_ids[0]}` : 'Bewerken vóór verzenden'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {bewerkModal.mode === 'single'
                    ? <>Eigen tekst voor deze ene factuur. </>
                    : <>Pas onderwerp en bericht aan voor <strong className="text-[#002FA7]">{bewerkModal.fac_ids.length}</strong> herinnering(en). </>
                  }
                  Placeholders: <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded">{'{klant}'}</code> <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded">{'{document_nr}'}</code> <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded">{'{outstanding}'}</code> <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded">{'{bedrijfsnaam}'}</code>
                </p>
              </div>
              <button onClick={() => setBewerkModal(null)} className="p-1 hover:bg-gray-200 rounded" data-testid="bewerk-modal-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-1.5">Onderwerp</label>
                <input
                  type="text"
                  className="field-input w-full"
                  value={bewerkModal.onderwerp}
                  onChange={e => setBewerkModal(p => ({ ...p, onderwerp: e.target.value }))}
                  data-testid="bewerk-modal-onderwerp"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-1.5">Bericht (HTML &lt;br&gt; ondersteund)</label>
                <textarea
                  className="field-input w-full font-mono text-sm"
                  rows={10}
                  value={bewerkModal.tekst}
                  onChange={e => setBewerkModal(p => ({ ...p, tekst: e.target.value }))}
                  data-testid="bewerk-modal-tekst"
                />
              </div>

              {/* Extra ontvangers (CC) — bv. boekhouder, tweede klant-contact */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-1.5">
                  Extra ontvangers in CC <span className="text-gray-400 normal-case font-normal">(optioneel — komma-gescheiden)</span>
                </label>
                <input
                  type="text"
                  className="field-input w-full"
                  placeholder="boekhouder@firma.be, manager@firma.be"
                  value={bewerkModal.cc_emails || ''}
                  onChange={e => setBewerkModal(p => ({ ...p, cc_emails: e.target.value }))}
                  data-testid="bewerk-modal-cc-emails"
                />
                {klanten.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className="text-[10px] text-gray-500">Snel toevoegen klant:</span>
                    <select
                      className="text-[11px] px-2 py-1 border border-gray-300 rounded bg-white max-w-[200px]"
                      onChange={e => {
                        if (!e.target.value) return;
                        const huidig = (bewerkModal.cc_emails || '').trim();
                        const sep = huidig && !huidig.endsWith(',') ? ', ' : '';
                        setBewerkModal(p => ({ ...p, cc_emails: (huidig + sep + e.target.value).trim() }));
                        e.target.value = '';
                      }}
                      data-testid="bewerk-modal-add-klant-email"
                      defaultValue=""
                    >
                      <option value="">-- Kies klant --</option>
                      {klanten.filter(k => k.email && k.email.includes('@')).map(k => (
                        <option key={k.id} value={k.email}>
                          {(k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim() || k.email).slice(0, 30)} — {k.email}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Plannen voor later toggle + datetime picker */}
              <div className="bg-purple-50 border border-purple-200 rounded p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 cursor-pointer accent-purple-600"
                    checked={bewerkModal.plannen}
                    onChange={e => setBewerkModal(p => ({ ...p, plannen: e.target.checked }))}
                    data-testid="bewerk-modal-plannen-toggle"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-purple-900 flex items-center gap-1.5">
                      <CalendarPlus size={14} weight="bold" /> Plannen voor later
                    </div>
                    <div className="text-[11px] text-purple-700 mt-0.5">
                      Verzend deze herinnering(en) automatisch op een toekomstige datum/tijd. De scheduler controleert elke 5 min.
                    </div>
                  </div>
                </label>
                {bewerkModal.plannen && (
                  <div className="mt-3 pl-6">
                    <label className="text-xs font-bold uppercase tracking-wider text-purple-700 block mb-1.5">Verzenden op</label>
                    <input
                      type="datetime-local"
                      className="field-input w-full max-w-xs"
                      value={bewerkModal.plan_datum}
                      onChange={e => setBewerkModal(p => ({ ...p, plan_datum: e.target.value }))}
                      min={new Date().toISOString().slice(0, 16)}
                      data-testid="bewerk-modal-plan-datum"
                    />
                    <div className="text-[10px] text-purple-600 mt-1 italic">
                      Tijdzone: Europe/Brussels — wordt automatisch verstuurd binnen 5 min na dit tijdstip
                    </div>
                  </div>
                )}
              </div>

              <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 p-2 rounded">
                💡 De aangepaste tekst wordt voor <strong>elke {bewerkModal.fac_ids.length === 1 ? 'geselecteerde factuur' : `${bewerkModal.fac_ids.length} geselecteerde facturen`}</strong> gebruikt. Placeholders worden automatisch ingevuld per klant.
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button onClick={() => setBewerkModal(null)} className="btn btn-secondary" data-testid="bewerk-modal-annuleren">Annuleren</button>
              <button
                onClick={verzendAangepast}
                disabled={sending || !bewerkModal.onderwerp.trim() || !bewerkModal.tekst.trim() || (bewerkModal.plannen && !bewerkModal.plan_datum)}
                className={`btn inline-flex items-center gap-1.5 ${bewerkModal.plannen ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'btn-primary'}`}
                data-testid="bewerk-modal-verzenden"
              >
                {bewerkModal.plannen ? (
                  <><CalendarPlus size={14} weight="bold" /> {sending ? 'Plannen...' : `Plannen voor ${bewerkModal.fac_ids.length}`}</>
                ) : (
                  <><Check size={14} weight="bold" /> {sending ? 'Versturen...' : `Verzenden naar ${bewerkModal.fac_ids.length}`}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
